import { GoogleLogin } from "@react-oauth/google";
import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth.js";

const LoginPage = () => {
  const { login, googleAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/";

  const handleGoogleSuccess = async ({ credential }) => {
    try {
      const data = await googleAuth(credential);
      if (data.status === "onboarding") {
        sessionStorage.setItem("google_onboarding", JSON.stringify(data));
        navigate("/register/google", { replace: true });
      } else {
        navigate(from, { replace: true });
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const [fields, setFields] = useState({ username: "", password: "" });
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    setFields((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(fields.username, fields.password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-sm pt-8">
      <Helmet>
        <title>Sign In — QuizFreak</title>
        <meta name="description" content="Sign in to your QuizFreak account to track your scores and compete on the leaderboard." />
        <link rel="canonical" href="https://quizfreak.com/v2/login/" />
      </Helmet>
      <h1 className="text-3xl font-semibold text-white">Sign in</h1>
      <p className="mt-1 text-sm text-white/60">
        No account?{" "}
        <Link to="/register" className="text-brand-glow hover:underline">
          Create one
        </Link>
      </p>

      <form onSubmit={handleSubmit} className="mt-8 space-y-4">
        <div>
          <label className="block text-sm font-medium text-white/80" htmlFor="username">
            Username
          </label>
          <input
            id="username"
            name="username"
            type="text"
            autoComplete="username"
            required
            value={fields.username}
            onChange={handleChange}
            className="mt-1 w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-white placeholder-white/30 focus:border-brand-glow/60 focus:outline-none"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-white/80" htmlFor="password">
            Password
          </label>
          <input
            id="password"
            name="password"
            type="password"
            autoComplete="current-password"
            required
            value={fields.password}
            onChange={handleChange}
            className="mt-1 w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-white placeholder-white/30 focus:border-brand-glow/60 focus:outline-none"
          />
        </div>

        {error && <p className="text-sm text-red-300">{error}</p>}

        <button
          type="submit"
          disabled={submitting}
          className="w-full rounded-full bg-brand-glow px-6 py-3 text-sm font-semibold text-white disabled:opacity-50"
        >
          {submitting ? "Signing in…" : "Sign in"}
        </button>
      </form>

      <div className="mt-6 flex items-center gap-3">
        <div className="h-px flex-1 bg-white/10" />
        <span className="text-xs text-white/40">or</span>
        <div className="h-px flex-1 bg-white/10" />
      </div>

      <div className="mt-4 flex justify-center">
        <GoogleLogin
          onSuccess={handleGoogleSuccess}
          onError={() => setError("Google sign-in failed. Please try again.")}
          theme="filled_black"
          shape="pill"
          text="signin_with"
        />
      </div>
    </div>
  );
};

export default LoginPage;
